// ============================================================================
// Editor de tienda — panel de controles.
//
// Renderiza las opciones del elemento seleccionado y escribe los cambios
// directamente sobre el EditorState (estado local; NO BD). Cuando una opción
// está bloqueada por plan, muestra la etiqueta de candado + "Mejorar plan".
// ============================================================================

import {
  iconoCandado,
  iconoCategorias,
  iconoEditar,
  iconoEliminar,
  iconoEmpresa,
  iconoImagen,
  iconoPagina,
  iconoProductos,
  iconoPublicaciones,
  iconoSubir,
  iconoUsuario,
  iconoVariantes,
  mostrarToast,
} from '@plataforma/ui';
import { escaparHtml } from '@plataforma/utils';

import {
  BORDES_OPCIONES,
  ESPACIADOS_VISUALES,
  ESTILOS_BOTONES,
  ESTILOS_TARJETAS,
  HEADER_VARIANTES,
  SOMBRAS_VISUALES,
  TEMAS_PREDETERMINADOS,
  TIPOGRAFIAS_VISUALES,
  VARIANTES_CATEGORIAS,
  VARIANTES_CONTACTO,
  VARIANTES_FOOTER,
  VARIANTES_PRODUCTOS,
  VARIANTES_PROPUESTA,
  VARIANTES_TESTIMONIOS,
  evaluarBloqueo,
  etiquetaBloqueo,
} from './temas.js';

// Columna de `empresa_perfil` que actualiza cada control de imagen.
export const CAMPO_PERFIL_POR_IDENTIDAD = {
  logo: 'logo_url',
  logo_blanco: 'logo_blanco_url',
  favicon: 'favicon_url',
};

const ETIQUETA_SECCION = {
  banner_principal: 'Banner principal',
  categorias: 'Categorías destacadas',
  propuesta_valor: 'Propuesta de valor',
  productos_destacados: 'Productos destacados',
  productos_nuevos: 'Productos nuevos',
  promociones: 'Promociones',
  novedades: 'Novedades',
  testimonios: 'Testimonios',
  footer: 'Pie de página',
};

// ---------------------------------------------------------------------------
// Tab "Elementos" — índice/navegador visual de las partes editables de la
// tienda. `renderControlesElementos` lo dibuja SIEMPRE (descubrimiento), y
// debajo muestra los controles del elemento seleccionado. Cada `id` coincide
// con el `MAPA_ELEMENTOS` de preview.js (header/categorias/productos/...): la
// selección en el panel y el clic en la preview comparten la MISMA fuente de
// verdad (`estado._elementoSeleccionado`) sin duplicar estado.
//
// `gestion` = ruta del módulo donde se configura el CONTENIDO de una sección
// (p. ej. banners/productos en "Página principal"). Las secciones con
// controles dedicados aquí (header, categorias) no llevan `gestion`: se
// activan sus controles actuales al seleccionarlas.
// ---------------------------------------------------------------------------
const GRUPOS_ELEMENTOS = [
  { id: 'cabecera', nombre: 'Cabecera' },
  { id: 'pagina_principal', nombre: 'Página principal' },
  { id: 'estilo_general', nombre: 'Estilo general' },
  { id: 'pie', nombre: 'Pie de página' },
];

// Sub-secciones del grupo "ESTILO GENERAL" (editor visual profesional):
// IDENTIDAD VISUAL / FORMAS Y PROFUNDIDAD / ESPACIADO Y DISTRIBUCIÓN.
const SUBGRUPOS_ESTILO = [
  { id: 'identidad', nombre: 'Identidad visual' },
  { id: 'formas', nombre: 'Formas y profundidad' },
  { id: 'espaciado', nombre: 'Espaciado y distribución' },
];

export const ELEMENTOS_EDITABLES = [
  {
    id: 'header',
    grupo: 'cabecera',
    nombre: 'Cabecera',
    descripcion: 'Logo, navegación y acciones',
    icono: iconoPagina,
  },
  {
    id: 'banner',
    grupo: 'pagina_principal',
    nombre: 'Banner principal',
    descripcion: 'Imagen y contenido principal',
    icono: iconoImagen,
    gestion: { etiqueta: 'Página principal', ruta: '#/pagina-principal' },
  },
  {
    id: 'categorias',
    grupo: 'pagina_principal',
    nombre: 'Categorías destacadas',
    descripcion: 'Diseño y distribución',
    icono: iconoCategorias,
  },
  {
    id: 'propuesta_valor',
    grupo: 'pagina_principal',
    nombre: 'Propuesta de valor',
    descripcion: 'Confianza y beneficios',
    icono: iconoPublicaciones,
    gestion: { etiqueta: 'Página principal', ruta: '#/pagina-principal' },
  },
  {
    id: 'productos',
    grupo: 'pagina_principal',
    nombre: 'Productos destacados',
    descripcion: 'Productos que aparecen en la portada',
    icono: iconoProductos,
    gestion: { etiqueta: 'Productos', ruta: '#/productos' },
  },
  {
    id: 'testimonios',
    grupo: 'pagina_principal',
    nombre: 'Testimonios',
    descripcion: 'Opiniones de clientes',
    icono: iconoVariantes,
    gestion: { etiqueta: 'Página principal', ruta: '#/pagina-principal' },
  },
  {
    id: 'contacto',
    grupo: 'pagina_principal',
    nombre: 'Contacto',
    descripcion: 'Atención directa por WhatsApp',
    icono: iconoUsuario,
    gestion: { etiqueta: 'Mi empresa', ruta: '#/empresa' },
  },
  {
    id: 'footer',
    grupo: 'pie',
    nombre: 'Pie de página',
    descripcion: 'Enlaces e información del cierre',
    icono: iconoEmpresa,
    gestion: { etiqueta: 'Página principal', ruta: '#/pagina-principal' },
  },
  // ---- ESTILO GLOBAL (editor visual de apariencia global) ----------------
  // Única configuración por propiedad (sin duplicados con la pestaña
  // "Estilos"). `subgrupo` agrupa las cards del navegador en IDENTIDAD /
  // FORMAS / ESPACIADO. Bordes sigue siendo una única configuración global
  // (radio) que afecta por igual a tarjetas y botones: NO se crean estilos
  // separados para botones ni tarjetas.
  {
    id: 'tipografia',
    grupo: 'estilo_general',
    subgrupo: 'identidad',
    nombre: 'Tipografía',
    descripcion: 'Fuente y carácter global',
    icono: iconoPagina,
  },
  {
    id: 'sombras',
    grupo: 'estilo_general',
    subgrupo: 'formas',
    nombre: 'Sombras',
    descripcion: 'Profundidad de las superficies',
    icono: iconoImagen,
  },
  {
    id: 'bordes',
    grupo: 'estilo_general',
    subgrupo: 'formas',
    nombre: 'Bordes',
    descripcion: 'Radio de esquinas global',
    icono: iconoCategorias,
  },
  {
    id: 'espaciado',
    grupo: 'estilo_general',
    subgrupo: 'espaciado',
    nombre: 'Espaciado',
    descripcion: 'Separación entre secciones',
    icono: iconoUsuario,
  },
];

function opcionSimple({ id, nombre, activo, bloqueado }) {
  return `
    <button type="button" class="pf-ed-opcion pf-ed-opcion--simple${activo ? ' pf-ed-opcion--activo' : ''}${bloqueado ? ' pf-ed-opcion--bloqueado' : ''}"
      data-opcion="${id}" ${bloqueado ? 'disabled' : ''}>
      <span class="pf-ed-opcion__nombre">${escaparHtml(nombre)}</span>
      ${activo ? '<span class="pf-ed-opcion__check">✓</span>' : ''}
    </button>
  `;
}

function campoColor(estado, clave, etiqueta) {
  return `
    <div class="pf-ed-campo">
      <label>${escaparHtml(etiqueta)}</label>
      <input type="color" data-color="${clave}" value="${escaparHtml(estado.tema[clave])}" />
    </div>
  `;
}

function bloqueoPlan(planMinimo, hrefMejorarPlan) {
  return `
    <div class="pf-ed-bloqueo">
      <span class="pf-ed-bloqueo__icono">${iconoCandado()}</span>
      <div class="pf-ed-bloqueo__texto">
        <span>${etiquetaBloqueo(planMinimo)}</span>
      </div>
      <a href="${hrefMejorarPlan}" class="pf-boton pf-boton--primario pf-boton--chico">Mejorar plan</a>
    </div>
  `;
}

function filaImagenIdentidad(id, nombre, url, fondo) {
  // Estado de cada campo es independiente de los otros: si hay URL →
  // preview + Cambiar/Eliminar; si no → placeholder + "Seleccionar archivo".
  // "Cambiar" y "Seleccionar archivo" abren el MISMO input file oculto
  // (data-cambiar); "Eliminar" solo limpia la URL en el EditorState — el
  // vacío se persiste al pulsar "Guardar cambios", nunca borra Storage.
  const acciones = url
    ? `
      <button type="button" class="pf-ed-identidad__btn pf-boton pf-boton--secundario pf-boton--chico"
        data-cambiar="${id}" aria-label="Cambiar ${escaparHtml(nombre)}" title="Cambiar ${escaparHtml(nombre)}">
        ${iconoEditar()}<span>Cambiar</span>
      </button>
      <button type="button" class="pf-ed-identidad__btn pf-boton pf-boton--peligro pf-boton--chico"
        data-eliminar="${id}" aria-label="Eliminar ${escaparHtml(nombre)}" title="Eliminar ${escaparHtml(nombre)}">
        ${iconoEliminar()}<span>Eliminar</span>
      </button>`
    : `
      <button type="button" class="pf-ed-identidad__btn pf-boton pf-boton--primario pf-boton--chico"
        data-cambiar="${id}" aria-label="Seleccionar archivo de ${escaparHtml(nombre)}" title="Seleccionar archivo: ${escaparHtml(nombre)}">
        ${iconoSubir()}<span>Seleccionar archivo</span>
      </button>`;

  return `
    <div class="pf-ed-identidad__fila">
      <div class="pf-ed-identidad__img" style="background:${fondo}">
        ${
          url
            ? `<img src="${escaparHtml(url)}" alt="Vista previa de ${escaparHtml(nombre)}" />`
            : `<span class="pf-ed-identidad__img-vacio">${iconoImagen()}</span>`
        }
      </div>
      <div class="pf-ed-identidad__info">
        <p class="pf-ed-identidad__etiqueta">${escaparHtml(nombre)}</p>
        <input type="file" class="pf-ed-identidad__archivo" data-identidad="${id}"
          accept="image/jpeg,image/png,image/webp"
          aria-label="${escaparHtml(nombre)}: ${url ? 'cambiar archivo' : 'seleccionar archivo'}" hidden />
        <div class="pf-ed-identidad__acciones">${acciones}</div>
      </div>
    </div>
  `;
}

function grupoControlesHeader(estado, tieneFeature, href) {
  const bloqueados = HEADER_VARIANTES.filter(
    (h) => evaluarBloqueo(estado.empresa, h.plan, tieneFeature).bloqueado,
  );
  return `
    <div class="pf-ed-grupo">
      <p class="pf-ed-grupo__titulo">Variante de header</p>
      <div class="pf-ed-opciones pf-ed-opciones--header">
        ${HEADER_VARIANTES.map((h) => {
          const { bloqueado } = evaluarBloqueo(estado.empresa, h.plan, tieneFeature);
          const activo = estado.editor.header === h.id;
          return `
            <button type="button"
              class="pf-ed-header-preview${activo ? ' pf-ed-header-preview--activo' : ''}${bloqueado ? ' pf-ed-header-preview--bloqueado' : ''}"
              data-opcion="${h.id}"
              ${bloqueado ? 'disabled' : ''}
              aria-label="${escaparHtml(h.nombre)}: ${escaparHtml(h.descripcion)}"
              title="${escaparHtml(h.nombre)}: ${escaparHtml(h.descripcion)}">
              <span class="pf-ed-header-preview__visual" data-header="${h.id}"></span>
              <span class="pf-ed-header-preview__nombre">${escaparHtml(h.nombre)}</span>
              <span class="pf-ed-header-preview__descripcion">${escaparHtml(h.descripcion)}</span>
              ${activo ? '<span class="pf-ed-header-preview__check" aria-hidden="true">✓</span>' : ''}
              ${bloqueado ? `<span class="pf-ed-header-preview__candado" aria-hidden="true">${iconoCandado()}</span>` : ''}
            </button>
          `;
        }).join('')}
      </div>
      ${bloqueados.length ? bloqueoPlan('Medio', href) : ''}
    </div>
  `;
}

function grupoControlesCategorias(estado, tieneFeature, href) {
  const bloqueadas = VARIANTES_CATEGORIAS.filter(
    (v) => evaluarBloqueo(estado.empresa, v.plan, tieneFeature).bloqueado,
  );
  return `
    <div class="pf-ed-grupo">
      <p class="pf-ed-grupo__titulo">Variante de categorías</p>
      <p class="pf-ed-aviso">Elige cómo se componen las categorías destacadas de la portada. Cambia al instante en la vista previa.</p>
      <div class="pf-ed-opciones pf-ed-opciones--categorias">
        ${VARIANTES_CATEGORIAS.map((v) => {
          const { bloqueado } = evaluarBloqueo(estado.empresa, v.plan, tieneFeature);
          const activo = estado.editor.categorias_variante === v.id;
          return `
            <button type="button"
              class="pf-ed-categorias-preview${activo ? ' pf-ed-categorias-preview--activo' : ''}${bloqueado ? ' pf-ed-categorias-preview--bloqueado' : ''}"
              data-opcion="${v.id}"
              ${bloqueado ? 'disabled' : ''}
              aria-label="${escaparHtml(v.nombre)}: ${escaparHtml(v.descripcion)}"
              title="${escaparHtml(v.nombre)}: ${escaparHtml(v.descripcion)}">
              <span class="pf-ed-categorias-preview__visual" data-categorias="${v.id}">
                <span class="pf-ed-mini-rect"></span><span class="pf-ed-mini-rect"></span><span class="pf-ed-mini-rect"></span><span class="pf-ed-mini-rect"></span>
              </span>
              <span class="pf-ed-categorias-preview__nombre">${escaparHtml(v.nombre)}</span>
              <span class="pf-ed-categorias-preview__descripcion">${escaparHtml(v.descripcion)}</span>
              ${activo ? '<span class="pf-ed-categorias-preview__check" aria-hidden="true">✓</span>' : ''}
              ${bloqueado ? `<span class="pf-ed-categorias-preview__candado" aria-hidden="true">${iconoCandado()}</span>` : ''}
            </button>
          `;
        }).join('')}
      </div>
      ${bloqueadas.length ? bloqueoPlan('Medio', href) : ''}
    </div>
  `;
}

function grupoControlesBotones(estado, tieneFeature, href) {
  const bloqueados = ESTILOS_BOTONES.filter(
    (b) => evaluarBloqueo(estado.empresa, b.plan, tieneFeature).bloqueado,
  );
  return `
    <div class="pf-ed-grupo">
      <p class="pf-ed-grupo__titulo">Estilo de botones</p>
      <div class="pf-ed-opciones">
        ${ESTILOS_BOTONES.map((b) => {
          const { bloqueado } = evaluarBloqueo(estado.empresa, b.plan, tieneFeature);
          return opcionSimple({
            id: b.id,
            nombre: b.nombre,
            activo: estado.editor.botones.estilo === b.id,
            bloqueado,
          });
        }).join('')}
      </div>
      ${bloqueados.length ? bloqueoPlan('Medio', href) : ''}
      <div class="pf-campo">
        <label>Radio (px)</label>
        <input type="number" data-botones="radio" min="0" max="32" value="${estado.editor.botones.radio}" />
      </div>
    </div>
  `;
}

function grupoControlesTarjetas(estado, tieneFeature, href) {
  const bloqueados = ESTILOS_TARJETAS.filter(
    (t) => evaluarBloqueo(estado.empresa, t.plan, tieneFeature).bloqueado,
  );
  return `
    <div class="pf-ed-grupo">
      <p class="pf-ed-grupo__titulo">Estilo de tarjetas</p>
      <div class="pf-ed-opciones">
        ${ESTILOS_TARJETAS.map((t) => {
          const { bloqueado } = evaluarBloqueo(estado.empresa, t.plan, tieneFeature);
          return opcionSimple({
            id: t.id,
            nombre: t.nombre,
            activo: estado.editor.tarjetas.estilo === t.id,
            bloqueado,
          });
        }).join('')}
      </div>
      ${bloqueados.length ? bloqueoPlan('Medio', href) : ''}
      <div class="pf-campo">
        <label>Radio (px)</label>
        <input type="number" data-tarjetas="radio" min="0" max="32" value="${estado.editor.tarjetas.radio}" />
      </div>
    </div>
  `;
}

function grupoControlesSecciones(estado) {
  const orden = estado.editor.seccion_orden;
  return `
    <div class="pf-ed-grupo">
      <p class="pf-ed-grupo__titulo">Orden de secciones</p>
      <p class="pf-ed-aviso">Usa las flechas para reordenar la portada.</p>
      <div class="pf-ed-orden" data-orden>
        ${orden
          .map(
            (codigo, i) => `
          <div class="pf-ed-orden__item" data-codigo="${escaparHtml(codigo)}">
            <span class="pf-ed-orden__nombre">${escaparHtml(ETIQUETA_SECCION[codigo] ?? codigo)}</span>
            <span class="pf-ed-orden__flechas">
              <button type="button" class="pf-boton pf-boton--secundario pf-boton--chico" data-mover="-1" ${i === 0 ? 'disabled' : ''}>↑</button>
              <button type="button" class="pf-boton pf-boton--secundario pf-boton--chico" data-mover="1" ${i === orden.length - 1 ? 'disabled' : ''}>↓</button>
            </span>
          </div>`,
          )
          .join('')}
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Selector visual de variantes de una sección (presentación). Reutiliza el
// mismo patrón de tarjeta-miniatura de header/categorías, pero con un esquema
// mínimo ("data-mini") distinto por familia que se compone en CSS.
// `minis` = número de bloques del esquema (1 para minimal/centrado, 3-4 resto).
// ---------------------------------------------------------------------------
function grupoVariantes({ catalogo, etiqueta, aviso, clave, prefijo, estado, tieneFeature, href }) {
  const bloqueadas = catalogo.filter(
    (v) => evaluarBloqueo(estado.empresa, v.plan, tieneFeature).bloqueado,
  );
  const activo = estado.editor[clave]?.variante;
  return `
    <div class="pf-ed-grupo">
      <p class="pf-ed-grupo__titulo">${escaparHtml(etiqueta)}</p>
      ${aviso ? `<p class="pf-ed-aviso">${escaparHtml(aviso)}</p>` : ''}
      <div class="pf-ed-opciones pf-ed-opciones--seccion">
        ${catalogo
          .map((v) => {
            const { bloqueado } = evaluarBloqueo(estado.empresa, v.plan, tieneFeature);
            const esActivo = activo === v.id;
            const minis = v.caracteristicas?.minimal ? 1 : v.caracteristicas?.principal ? 3 : 4;
            return `
              <button type="button"
                class="pf-ed-sec-preview${esActivo ? ' pf-ed-sec-preview--activo' : ''}${bloqueado ? ' pf-ed-sec-preview--bloqueado' : ''}"
                data-opcion="${v.id}"
                ${bloqueado ? 'disabled' : ''}
                aria-label="${escaparHtml(v.nombre)}: ${escaparHtml(v.descripcion)}"
                title="${escaparHtml(v.nombre)}: ${escaparHtml(v.descripcion)}">
                <span class="pf-ed-sec-preview__visual" data-mini="${prefijo}:${v.id}">
                  ${Array.from({ length: minis }, () => '<i class="pf-ed-mini-rect"></i>').join('')}
                </span>
                <span class="pf-ed-sec-preview__nombre">${escaparHtml(v.nombre)}</span>
                <span class="pf-ed-sec-preview__descripcion">${escaparHtml(v.descripcion)}</span>
                ${esActivo ? '<span class="pf-ed-sec-preview__check" aria-hidden="true">✓</span>' : ''}
                ${bloqueado ? `<span class="pf-ed-sec-preview__candado" aria-hidden="true">${iconoCandado()}</span>` : ''}
              </button>`;
          })
          .join('')}
      </div>
      ${bloqueadas.length ? bloqueoPlan('Medio', href) : ''}
    </div>
  `;
}

// Nota honesta "Caso B": qué gestiona Personalizar tienda vs el módulo real.
function notaGestion(texto, etiqueta, ruta) {
  return `
    <div class="pf-ed-seccion-info">
      <div class="pf-ed-seccion-info__cuerpo">
        <p class="pf-ed-seccion-info__nota">${escaparHtml(texto)}
          <a href="${ruta}" class="pf-ed-seccion-info__enlace">${escaparHtml(etiqueta)}</a>.</p>
      </div>
    </div>`;
}

function grupoControlesBanner(estado, _tieneFeature, _href) {
  const banner = estado.editor.banner;
  const alineaciones = [
    { id: 'left', nombre: 'Izquierda' },
    { id: 'center', nombre: 'Centro' },
    { id: 'right', nombre: 'Derecha' },
  ];
  return `
    <div class="pf-ed-grupo">
      <p class="pf-ed-grupo__titulo">Presentación del banner</p>
      <p class="pf-ed-aviso">Controla cómo se muestra el contenido principal al entrar a tu tienda. Las imágenes y textos se editan en "Página principal".</p>
      <div class="pf-campo">
        <label>Texto del botón principal</label>
        <input type="text" data-banner="texto_boton" maxlength="30" value="${escaparHtml(banner.texto_boton)}" />
      </div>
      <div class="pf-campo">
        <label>Etiqueta superior</label>
        <input type="text" data-banner="etiqueta" maxlength="40" value="${escaparHtml(banner.etiqueta)}" placeholder="Ej.: Nueva colección" />
      </div>
      <div class="pf-ed-opciones pf-ed-opciones--seccion pf-ed-opciones--lineal">
        ${alineaciones
          .map(
            (a) => `
          <button type="button" class="pf-ed-opcion pf-ed-opcion--simple${banner.alineacion === a.id ? ' pf-ed-opcion--activo' : ''}"
            data-banner="alineacion" data-valor="${a.id}">
            <span class="pf-ed-opcion__nombre">${escaparHtml(a.nombre)}</span>
            ${banner.alineacion === a.id ? '<span class="pf-ed-opcion__check">✓</span>' : ''}
          </button>`,
          )
          .join('')}
      </div>
      <div class="pf-campo">
        <label>Oscurecido de la imagen (${banner.overlay}%)</label>
        <input type="range" min="10" max="78" step="1" data-banner="overlay" value="${banner.overlay}" />
      </div>
      <label class="pf-ed-check">
        <input type="checkbox" data-banner="autoplay" ${banner.autoplay ? 'checked' : ''} />
        <span>Reproducir automáticamente si hay varios banners</span>
      </label>
    </div>
    ${notaGestion('Las imágenes, títulos y enlaces de cada banner se administran en "Página principal".', 'Página principal', '#/pagina-principal')}
  `;
}

function grupoControlesPropuesta(estado, tieneFeature, href) {
  const grupo = grupoVariantes({
    catalogo: VARIANTES_PROPUESTA,
    etiqueta: 'Variante de propuesta de valor',
    aviso: 'Elige cómo se componen los puntos de confianza de la portada.',
    clave: 'propuesta_valor',
    prefijo: 'propuesta',
    estado,
    tieneFeature,
    href,
  });
  const propuesta = estado.editor.propuesta_valor;
  return `
    <div class="pf-campo">
      <label>Título de sección</label>
      <input type="text" data-propuesta="titulo" maxlength="60" value="${escaparHtml(propuesta.titulo)}" placeholder="¿Por qué comprar aquí?" />
    </div>
    ${grupo}
    ${notaGestion('Los puntos de confianza (título y descripción) se administran en', 'Página principal', '#/pagina-principal')}
  `;
}

function grupoControlesProductos(estado, tieneFeature, href) {
  const productos = estado.editor.productos;
  const grupo = grupoVariantes({
    catalogo: VARIANTES_PRODUCTOS,
    etiqueta: 'Distribución de productos',
    aviso: 'Cómo se componen las tarjetas de producto en la portada.',
    clave: 'productos',
    prefijo: 'productos',
    estado,
    tieneFeature,
    href,
  });
  return `
    <div class="pf-campo">
      <label>Título de sección</label>
      <input type="text" data-productos="titulo" maxlength="60" value="${escaparHtml(productos.titulo)}" placeholder="Productos destacados" />
    </div>
    <div class="pf-campo">
      <label>Texto auxiliar</label>
      <input type="text" data-productos="eyebrow" maxlength="40" value="${escaparHtml(productos.eyebrow)}" placeholder="Selección" />
    </div>
    ${grupo}
    <label class="pf-ed-check">
      <input type="checkbox" data-productos="mostrar_ver_todo" ${productos.mostrar_ver_todo ? 'checked' : ''} />
      <span>Mostrar enlace "Ir al catálogo"</span>
    </label>
    ${notaGestion('La cantidad de productos y cuáles salen destacados se controla en', 'Productos', '#/productos')}
  `;
}

function grupoControlesTestimonios(estado, tieneFeature, href) {
  const grupo = grupoVariantes({
    catalogo: VARIANTES_TESTIMONIOS,
    etiqueta: 'Variante de testimonios',
    aviso: 'Elige la disposición de las opiniones de tus clientes.',
    clave: 'testimonios',
    prefijo: 'testimonios',
    estado,
    tieneFeature,
    href,
  });
  const testimonios = estado.editor.testimonios;
  return `
    <div class="pf-campo">
      <label>Título de sección</label>
      <input type="text" data-testimonios="titulo" maxlength="60" value="${escaparHtml(testimonios.titulo)}" />
    </div>
    ${grupo}
    ${notaGestion('Las opiniones de clientes se administran en', 'Página principal', '#/pagina-principal')}
  `;
}

function grupoControlesContacto(estado, tieneFeature, href) {
  const contacto = estado.editor.contacto;
  const grupo = grupoVariantes({
    catalogo: VARIANTES_CONTACTO,
    etiqueta: 'Distribución de contacto',
    aviso: 'Cómo se presenta el acceso directo por WhatsApp.',
    clave: 'contacto',
    prefijo: 'contacto',
    estado,
    tieneFeature,
    href,
  });
  return `
    <div class="pf-campo">
      <label>Título</label>
      <input type="text" data-contacto="titulo" maxlength="60" value="${escaparHtml(contacto.titulo)}" />
    </div>
    <div class="pf-campo">
      <label>Texto</label>
      <input type="text" data-contacto="texto" maxlength="120" value="${escaparHtml(contacto.texto)}" />
    </div>
    ${grupo}
    ${notaGestion('El número de WhatsApp, dirección y horario se administran en', 'Mi empresa', '#/empresa')}
  `;
}

function grupoControlesFooter(estado, tieneFeature, href) {
  const grupo = grupoVariantes({
    catalogo: VARIANTES_FOOTER,
    etiqueta: 'Variante del pie de página',
    aviso: 'Cómo se presenta la marca, el contacto y las redes al pie de la tienda.',
    clave: 'footer',
    prefijo: 'footer',
    estado,
    tieneFeature,
    href,
  });
  return `
    ${grupo}
    ${notaGestion('La descripción, contacto, horario y redes se administran en', 'Mi empresa', '#/empresa')}
  `;
}

/**
 * Estilos del grupo ESTILO GENERAL del editor visual (Tipografía, Tamaño de
 * texto, Bordes, Sombras, Botones, Tarjetas, Espaciado, Densidad y Ancho del
 * contenido). Cada elemento se muestra como una colección de CARDS visuales
 * seleccionables (`.pf-ed-sec-preview`) en lugar de los antiguos selects de
 * "Formas y tipografía". La selección actualiza `estado.tema` / `estado.editor`
 * y el preview en vivo.
 *
 * SIN bloqueo por plan por ahora: todas las cards disponibles (plan: null en
 * los catálogos), la estructura queda lista para activar gating más adelante.
 * @param {EditorState} estado
 * @param {function} tieneFeature
 * @param {string} href
 * @param {string} id - uno de los ids del grupo estilo_general
 * @returns {string} HTML
 */
function renderControlesEstiloGeneral(estado, _tieneFeature, _href, id) {
  const encabezado = (titulo, aviso) => `
    <div class="pf-ed-grupo">
      <p class="pf-ed-grupo__titulo">${escaparHtml(titulo)}</p>
      ${aviso ? `<p class="pf-ed-aviso">${escaparHtml(aviso)}</p>` : ''}
  `;
  const cerrarGrupo = `</div>`;

  /* ---------- TIPOGRAFÍA ---------- */
  if (id === 'tipografia') {
    return `
      ${encabezado(
        'Tipografía',
        'Define el carácter tipográfico de toda la tienda. Elige la fuente que mejor represente tu marca.',
      )}
      ${renderTarjetasEstilo(
        TIPOGRAFIAS_VISUALES,
        estado.tema.tipografia,
        (op) => `
        <span class="pf-ed-vis-tipografia" style="font-family:${op.familia}">
          <span class="pf-ed-vis-tipografia__muestra">${op.muestra}</span>
          <span class="pf-ed-vis-tipografia__frase">${op.frase}</span>
        </span>`,
      )}
      ${cerrarGrupo}`;
  }

  /* ---------- SOMBRAS ---------- */
  if (id === 'sombras') {
    return `
      ${encabezado(
        'Sombras',
        'Añade elevación y profundidad a las tarjetas y superficies de la tienda.',
      )}
      ${renderTarjetasEstilo(
        SOMBRAS_VISUALES,
        estado.tema.sombras,
        (op) => `
        <span class="pf-ed-vis-sombra">
          <i class="pf-ed-vis-sombra__caja" style="box-shadow:${op.css}"></i>
        </span>`,
      )}
      ${cerrarGrupo}`;
  }

  /* ---------- ESPACIADO ---------- */
  if (id === 'espaciado') {
    return `
      ${encabezado(
        'Espaciado',
        'Controla la separación vertical entre las secciones de la tienda.',
      )}
      ${renderTarjetasEstilo(
        ESPACIADOS_VISUALES,
        estado.tema.espaciado,
        (op) => `
        <span class="pf-ed-vis-espaciado" style="--pf-vis-esp-h:${Math.round(4 + op.densidad * 10)}px">
          <i></i><i></i><i></i><i></i>
        </span>`,
      )}
      ${cerrarGrupo}`;
  }

  /* ---------- BORDES (radio, configuración global única) ---------- */
  if (id === 'bordes') {
    const actual = String(estado.tema.radio_bordes);
    const esPersonalizado = !BORDES_OPCIONES.some((o) => String(o.id) === actual);
    const radio = Number(estado.tema.radio_bordes) || 0;

    return `
      ${encabezado(
        'Bordes',
        'Radio de esquinas global: se aplica por igual a tarjetas, botones e imágenes. Es una sola configuración.',
      )}
      ${renderTarjetasEstilo(
        BORDES_OPCIONES,
        actual,
        (op) => `
        <span class="pf-ed-vis-borde">
          <i class="pf-ed-vis-borde__caja" style="border-radius:${op.valor <= 32 ? `${op.valor}px` : '999px'}"></i>
          <em>${op.etiqueta}</em>
        </span>`,
      )}
      <div class="pf-ed-borde-custom${esPersonalizado ? ' pf-ed-borde-custom--activo' : ''}">
        <div class="pf-ed-borde-custom__preview">
          <i class="pf-ed-vis-borde__caja" style="border-radius:${radio <= 32 ? `${radio}px` : '999px'}"></i>
          <strong>Personalizado</strong>
          <span>${radio <= 32 ? `${radio} px` : 'Píldora'}</span>
        </div>
        <div class="pf-ed-borde-custom__control">
          <label for="pf-ed-radio-bordes">Radio de bordes</label>
          <input type="range" id="pf-ed-radio-bordes" data-tema="radio_bordes"
            min="0" max="32" step="1" value="${radio}" aria-label="Radio de bordes en píxeles" />
          <div class="pf-ed-borde-custom__valores">
            <input type="number" data-tema="radio_bordes" min="0" max="32" step="1"
              value="${radio}" aria-label="Radio de bordes (número)" />
            <span>px</span>
          </div>
        </div>
      </div>
      ${cerrarGrupo}`;
  }

  return '';
}

/**
 * Rejilla de CARDS seleccionables del editor visual (ESTILO GENERAL).
 * Reutiliza `.pf-ed-sec-preview` para que la selección active el mismo
 * `[data-opcion]` que las demás variantes y el preview en vivo.
 * @param {object[]} opciones - catálogo con {id, nombre, descripcion, ...
 * @param {string} seleccionado - id activo
 * @param {function(object):string} renderVisual - (opcion) => HTML del área visual
 * @returns {string} HTML
 */
function renderTarjetasEstilo(opciones, seleccionado, renderVisual) {
  return `
    <div class="pf-ed-opciones pf-ed-opciones--seccion">
      ${opciones
        .map((opcion) => {
          const esActivo = String(seleccionado) === String(opcion.id);
          return `
            <button type="button"
              class="pf-ed-sec-preview${esActivo ? ' pf-ed-sec-preview--activo' : ''}"
              data-opcion="${opcion.id}"
              aria-label="${escaparHtml(opcion.nombre)}: ${escaparHtml(opcion.descripcion ?? '')}"
              title="${escaparHtml(opcion.nombre)}">
              <span class="pf-ed-sec-preview__visual">${renderVisual(opcion)}</span>
              <span class="pf-ed-sec-preview__nombre">${escaparHtml(opcion.nombre)}</span>
              <span class="pf-ed-sec-preview__descripcion">${escaparHtml(opcion.descripcion ?? '')}</span>
              ${esActivo ? '<span class="pf-ed-sec-preview__check" aria-hidden="true">✓</span>' : ''}
            </button>`;
        })
        .join('')}
    </div>`;
}

/**
 * Detalle (controles o panel contextual) del elemento seleccionado.
 * Las secciones con controles dedicados en esta pestaña (header, categorias)
 * activan sus controles actuales; las demás (gestionadas en otros módulos)
 * muestran una tarjeta informativa honesta en lugar de un formulario ficticio.
 * @param {EditorState} estado
 * @param {function} tieneFeature
 * @param {string} href - ruta "Mejorar plan"
 * @param {string|null} id - elemento seleccionado
 * @returns {string} HTML
 */
function renderDetalleElemento(estado, tieneFeature, href, id) {
  if (id === 'header') return grupoControlesHeader(estado, tieneFeature, href);
  if (id === 'categorias') return grupoControlesCategorias(estado, tieneFeature, href);
  if (id === 'banner') return grupoControlesBanner(estado, tieneFeature, href);
  if (id === 'propuesta_valor') return grupoControlesPropuesta(estado, tieneFeature, href);
  if (id === 'productos') return grupoControlesProductos(estado, tieneFeature, href);
  if (id === 'testimonios') return grupoControlesTestimonios(estado, tieneFeature, href);
  if (id === 'contacto') return grupoControlesContacto(estado, tieneFeature, href);
  if (id === 'footer') return grupoControlesFooter(estado, tieneFeature, href);

  // Opciones de apariencia global (elementos del grupo ESTILO GENERAL):
  // se muestran como una colección de cards visuales seleccionables.
  if (ELEMENTOS_EDITABLES.some((e) => e.id === id && e.grupo === 'estilo_general')) {
    return renderControlesEstiloGeneral(estado, tieneFeature, href, id);
  }

  const elemento = ELEMENTOS_EDITABLES.find((e) => e.id === id);
  if (!elemento) {
    return `
      <div class="pf-ed-seccion-info pf-ed-seccion-info--vacio">
        <p class="pf-ed-seccion-info__vacio">Selecciona una parte de la tienda arriba para ver sus opciones.</p>
      </div>`;
  }
  return `
    <div class="pf-ed-seccion-info">
      <span class="pf-ed-seccion-info__icono" aria-hidden="true">${elemento.icono()}</span>
      <div class="pf-ed-seccion-info__cuerpo">
        <p class="pf-ed-seccion-info__nombre">${escaparHtml(elemento.nombre)}</p>
        <p class="pf-ed-seccion-info__descripcion">${escaparHtml(elemento.descripcion)}</p>
        ${
          elemento.gestion
            ? `<p class="pf-ed-seccion-info__nota">Aparece seleccionada en la vista previa. Su contenido se configura desde
                <a href="${elemento.gestion.ruta}" class="pf-ed-seccion-info__enlace">${escaparHtml(
                  elemento.gestion.etiqueta,
                )}</a>.</p>`
            : ''
        }
      </div>
    </div>`;
}

/**
 * Tab "Elementos" — navegador visual de todas las partes editables de la
 * tienda (Cabecera, Página principal y su contenido, Pie de página) + el
 * detalle del elemento seleccionado. Resuelve el problema de descubrimiento:
 * el usuario ve qué es editable sin tener que adivinar que debe hacer clic en
 * el preview. Clic -> `data-nav-elemento` -> `onSeleccionarElemento`.
 *
 * La selección comparte la ÚNICA fuente de verdad (`estado._elementoSeleccionado`)
 * con el clic directo en la preview (panel <-> preview sincronizados).
 * @param {EditorState} estado - debe tener `_elementoSeleccionado`
 * @param {function} tieneFeature
 * @param {string} hrefMejorarPlan
 * @returns {string} HTML
 */
export function renderControlesElementos(estado, tieneFeature, hrefMejorarPlan) {
  const seleccionado = estado._elementoSeleccionado;

  const itemHtml = (item) => `
    <button type="button"
      class="pf-ed-nav__item${seleccionado === item.id ? ' pf-ed-nav__item--activo' : ''}"
      data-nav-elemento="${item.id}"
      aria-pressed="${seleccionado === item.id ? 'true' : 'false'}">
      <span class="pf-ed-nav__icono" aria-hidden="true">${item.icono()}</span>
      <span class="pf-ed-nav__texto">
        <span class="pf-ed-nav__nombre">${escaparHtml(item.nombre)}</span>
        <span class="pf-ed-nav__descripcion">${escaparHtml(item.descripcion)}</span>
      </span>
      <span class="pf-ed-nav__chevron" aria-hidden="true">›</span>
    </button>`;

  const navegador = GRUPOS_ELEMENTOS.map((grupo) => {
    if (grupo.id === 'estilo_general') {
      // El grupo ESTILO GENERAL se subdivide en IDENTIDAD / FORMAS /
      // ESPACIADO para darle una jerarquía de editor visual profesional.
      const porSubgrupo = SUBGRUPOS_ESTILO.map((sub) => ({
        sub,
        items: ELEMENTOS_EDITABLES.filter(
          (e) => e.grupo === 'estilo_general' && e.subgrupo === sub.id,
        ),
      })).filter((bloque) => bloque.items.length > 0);

      return `
        <div class="pf-ed-nav__grupo">
          <p class="pf-ed-nav__grupo-titulo">${escaparHtml(grupo.nombre)}</p>
          ${porSubgrupo
            .map(
              ({ sub, items }) => `
            <p class="pf-ed-nav__subgrupo-titulo">${escaparHtml(sub.nombre)}</p>
            <div class="pf-ed-nav__lista">
              ${items.map(itemHtml).join('')}
            </div>`,
            )
            .join('')}
        </div>`;
    }

    const items = ELEMENTOS_EDITABLES.filter((e) => e.grupo === grupo.id);
    return `
      <div class="pf-ed-nav__grupo">
        <p class="pf-ed-nav__grupo-titulo">${escaparHtml(grupo.nombre)}</p>
        <div class="pf-ed-nav__lista">
          ${items.map(itemHtml).join('')}
        </div>
      </div>`;
  }).join('');

  const detalle = renderDetalleElemento(estado, tieneFeature, hrefMejorarPlan, seleccionado);

  return `
    <div class="pf-ed-controles">
      <div class="pf-ed-nav" aria-label="Partes editables de la tienda">${navegador}</div>
      <div class="pf-ed-nav__detalle">${detalle}</div>
    </div>`;
}

/**
 * Renderiza el panel de controles según el elemento seleccionado.
 * @param {EditorState} estado - debe tener `_elementoSeleccionado`
 * @param {function} tieneFeature - (empresa, clave) => boolean
 * @param {string} hrefMejorarPlan - ruta para "Mejorar plan"
 * @returns {string} HTML
 */
export function renderControles(estado, tieneFeature, hrefMejorarPlan) {
  const href = hrefMejorarPlan;
  const seleccionado = estado._elementoSeleccionado;

  if (seleccionado === 'header') {
    return `<div class="pf-ed-controles">${grupoControlesHeader(estado, tieneFeature, href)}</div>`;
  }
  if (seleccionado === 'categorias') {
    return `<div class="pf-ed-controles">${grupoControlesCategorias(estado, tieneFeature, href)}</div>`;
  }
  if (seleccionado === 'botones') {
    return `<div class="pf-ed-controles">${grupoControlesBotones(estado, tieneFeature, href)}</div>`;
  }
  if (seleccionado === 'tarjetas') {
    return `<div class="pf-ed-controles">${grupoControlesTarjetas(estado, tieneFeature, href)}</div>`;
  }
  if (seleccionado === 'secciones') {
    return `<div class="pf-ed-controles">${grupoControlesSecciones(estado)}</div>`;
  }

  return `
    <div class="pf-ed-controles">
      <div class="pf-ed-hint">
        Haz clic en un elemento de la vista previa (header, banner, categorías, productos, footer)
        para editarlo. También puedes cambiar la paleta de colores desde "Estilos".
      </div>
    </div>
  `;
}

/**
 * Renderiza el grupo de identidad (nombre de tienda y logos/favicon).
 * @returns {string} HTML
 */
export function renderControlesIdentidad(estado, _tieneFeature, _hrefMejorarPlan) {
  const perfil = estado.identidad.perfil;
  return `
    <div class="pf-ed-controles">
      <div class="pf-ed-grupo">
        <p class="pf-ed-grupo__titulo">Identidad de la tienda</p>
        <p class="pf-ed-aviso">El nombre y las imágenes de marca se muestran en tu tienda. Pulsa "Guardar cambios" para publicarlos.</p>
        <div class="pf-campo">
          <label for="pf-ed-nombre-tienda">Nombre de tienda</label>
          <input type="text" id="pf-ed-nombre-tienda" data-identidad="nombre" value="${escaparHtml(estado.identidad.nombre)}" maxlength="120" />
        </div>
        <div class="pf-ed-identidad">
          ${filaImagenIdentidad('logo', 'Logo', perfil.logo_url, 'var(--pf-color-fondo, #FFFFFF)')}
          ${filaImagenIdentidad('logo_blanco', 'Logo blanco (fondos de color)', perfil.logo_blanco_url, estado.tema.color_primario)}
          ${filaImagenIdentidad('favicon', 'Favicon', perfil.favicon_url, 'var(--pf-color-fondo, #FFFFFF)')}
        </div>
      </div>
    </div>
  `;
}

/**
 * Renderiza el grupo de paleta/preset + campos de color + formas y tipografía
 * (vista "Estilos"). Incluye los 10 colores del tema y los detalles visuales
 * (radio de bordes, tipografía, sombras, espaciado) que antes vivían en
 * "Apariencia".
 * @returns {string} HTML
 */
export function renderControlesEstilo(estado, tieneFeature, hrefMejorarPlan) {
  const href = hrefMejorarPlan;
  const bloqueados = TEMAS_PREDETERMINADOS.filter(
    (p) => evaluarBloqueo(estado.empresa, p.plan, tieneFeature).bloqueado,
  );
  return `
    <div class="pf-ed-controles">
      <div class="pf-ed-grupo">
        <p class="pf-ed-grupo__titulo">Paleta de colores</p>
        <div class="pf-ed-paleta-swatches">
          ${TEMAS_PREDETERMINADOS.map((p) => {
            const { bloqueado } = evaluarBloqueo(estado.empresa, p.plan, tieneFeature);
            const activo = estado.editor.tema_preset === p.id;
            return `
              <button type="button" class="pf-ed-swatch${activo ? ' pf-ed-swatch--activo' : ''}${bloqueado ? ' pf-ed-swatch--bloqueado' : ''}"
                data-preset="${p.id}" ${bloqueado ? 'disabled' : ''} title="${escaparHtml(p.nombre)}">
                <span style="background:${p.paleta.color_primario}"></span>
                <span style="background:${p.paleta.color_secundario}"></span>
                <span style="background:${p.paleta.color_fondo}"></span>
                ${activo ? '<span class="pf-ed-swatch__check">✓</span>' : ''}
              </button>`;
          }).join('')}
        </div>
        ${bloqueados.length ? bloqueoPlan('Medio', href) : ''}
        <div class="pf-ed-campos-color">
          ${[
            ['color_primario', 'Primario'],
            ['color_secundario', 'Secundario'],
            ['color_botones', 'Botones'],
            ['color_botones_hover', 'Botones hover'],
            ['color_texto', 'Texto'],
            ['color_header', 'Header'],
            ['color_footer', 'Footer'],
            ['color_enlaces', 'Enlaces'],
            ['color_fondo', 'Fondo'],
            ['color_tarjetas', 'Tarjetas'],
          ]
            .map(([k, et]) => campoColor(estado, k, et))
            .join('')}
        </div>
      </div>
    </div>
  `;
}

/**
 * Cablea los eventos del panel de controles recién renderizado.
 * @param {HTMLElement} contenedor - el nodo que contiene el HTML de controles
 * @param {EditorState} estado
 * @param {object} [opciones]
 * @param {{logo?:Function, logo_blanco?:Function, favicon?:Function}} [opciones.subirIdentidad]
 *   funciones (archivo) => Promise<{url, error}> para subir cada imagen de identidad.
 * @param {function(string):void} [opciones.onSeleccionarElemento]
 *   llamado con el id del elemento al hacer clic en un ítem del navegador
 *   "Elementos" (para seleccionar y desplazar la preview).
 */
export function cablearControles(contenedor, estado, opciones = {}) {
  contenedor.querySelectorAll('[data-nav-elemento]').forEach((el) => {
    el.addEventListener('click', () => {
      opciones.onSeleccionarElemento?.(el.dataset.navElemento);
    });
  });

  contenedor.querySelectorAll('[data-preset]').forEach((el) => {
    el.addEventListener('click', () => estado.aplicarPreset(el.dataset.preset));
  });

  contenedor.querySelectorAll('[data-color]').forEach((el) => {
    el.addEventListener('input', () => {
      estado.setTheme({ [el.dataset.color]: el.value });
    });
  });

  // Campo global "Radio de bordes" (control Personalizado de Bordes en
  // Elementos). Se aplica en vivo (`input`) para que el preview reaccione al
  // arrastrar el slider o teclear.
  contenedor.querySelectorAll('[data-tema]').forEach((el) => {
    const aplicar = () => {
      estado.setTheme({ [el.dataset.tema]: el.value });
    };
    el.addEventListener('input', aplicar);
    el.addEventListener('change', aplicar);
  });

  // Identidad: nombre (en vivo) y subida de imágenes (storage -> URL en estado).
  const subirIdentidad = opciones.subirIdentidad ?? {};
  contenedor.querySelectorAll('[data-identidad="nombre"]').forEach((el) => {
    el.addEventListener('input', () => {
      estado.setNombreTienda(el.value);
    });
  });

  // Botones "Seleccionar archivo" (sin imagen) y "Cambiar" (con imagen):
  // ambos abren el input file oculto de su fila.
  contenedor.querySelectorAll('[data-cambiar]').forEach((boton) => {
    boton.addEventListener('click', () => {
      const input = contenedor.querySelector(`input[data-identidad="${boton.dataset.cambiar}"]`);
      input?.click();
    });
  });

  // Botón "Eliminar": solo limpia la URL en el EditorState (estado en
  // memoria). "Guardar cambios" persiste el campo vacío en la BD. No se
  // borra el objeto de Storage: el modelo sigue siendo EDITOR -> ESTADO ->
  // GUARDAR, y un reboot no pierde nada.
  contenedor.querySelectorAll('[data-eliminar]').forEach((boton) => {
    boton.addEventListener('click', () => {
      const campo = CAMPO_PERFIL_POR_IDENTIDAD[boton.dataset.eliminar];
      if (!campo) return;
      estado.setImagenPerfil(campo, null);
      mostrarToast('Imagen eliminada. Pulsa "Guardar cambios" para publicar el cambio.', {
        tono: 'exito',
      });
    });
  });

  contenedor.querySelectorAll('[data-identidad]').forEach((el) => {
    if (el.dataset.identidad === 'nombre') return;
    el.addEventListener('change', async () => {
      const archivo = el.files?.[0];
      if (!archivo) return;
      const subir = subirIdentidad[el.dataset.identidad];
      const campo = CAMPO_PERFIL_POR_IDENTIDAD[el.dataset.identidad];
      if (!subir || !campo) return;

      const { url, error } = await subir(archivo);
      el.value = '';
      if (error) {
        mostrarToast(error.message ?? 'No se pudo subir la imagen.', { tono: 'peligro' });
        return;
      }
      estado.setImagenPerfil(campo, url);
      mostrarToast('Imagen lista. Pulsa "Guardar cambios" para publicarla.', { tono: 'exito' });
    });
  });

  const grupo = contenedor.querySelector('.pf-ed-controles');
  if (!grupo) return;

  grupo.querySelectorAll('[data-opcion]').forEach((el) => {
    el.addEventListener('click', () => {
      const opcion = el.dataset.opcion;
      switch (estado._elementoSeleccionado) {
        case 'header':
          estado.setHeader(opcion);
          break;
        case 'categorias':
          estado.setCategoriasVariante(opcion);
          break;
        case 'botones':
          estado.setBotones({ estilo: opcion });
          break;
        case 'tarjetas':
          estado.setTarjetas({ estilo: opcion });
          break;
        // ---- Estilo global (cards visuales del editor) --------------------
        case 'tipografia':
          estado.setTheme({ tipografia: opcion });
          break;
        case 'bordes':
          estado.setTheme({ radio_bordes: opcion });
          break;
        case 'sombras':
          estado.setTheme({ sombras: opcion });
          break;
        case 'espaciado':
          estado.setTheme({ espaciado: opcion });
          break;
        case 'propuesta_valor':
          estado.setPropuestaValor({ variante: opcion });
          break;
        case 'productos':
          estado.setProductos({ variante: opcion });
          break;
        case 'testimonios':
          estado.setTestimonios({ variante: opcion });
          break;
        case 'contacto':
          estado.setContacto({ variante: opcion });
          break;
        case 'footer':
          estado.setFooter({ variante: opcion });
          break;
        default:
          break;
      }
    });
  });

  // Controles de presentación del banner (texto, alineación, overlay, autoplay).
  grupo.querySelectorAll('[data-banner]').forEach((el) => {
    const aplicar = () => {
      const cambios = {};
      cambios[el.dataset.banner] =
        el.dataset.banner === 'overlay'
          ? Number(el.value)
          : el.dataset.banner === 'autoplay'
            ? el.checked
            : (el.dataset.valor ?? el.value);
      estado.setBanner(cambios);
    };
    if (el.dataset.banner === 'alineacion' || el.dataset.banner === 'autoplay') {
      el.addEventListener('click', aplicar);
    } else {
      el.addEventListener('input', aplicar);
    }
  });

  // Textos de sección (propuesta/productos/testimonios/contacto).
  const camposDeSeccion = {
    'data-propuesta': 'setPropuestaValor',
    'data-productos': 'setProductos',
    'data-testimonios': 'setTestimonios',
    'data-contacto': 'setContacto',
  };
  Object.entries(camposDeSeccion).forEach(([atributo, setter]) => {
    grupo.querySelectorAll(`[${atributo}]`).forEach((el) => {
      const aplicar = () => {
        const cambios = {};
        cambios[el.getAttribute(atributo)] = el.type === 'checkbox' ? el.checked : el.value;
        estado[setter](cambios);
      };
      if (el.type === 'checkbox') el.addEventListener('change', aplicar);
      else el.addEventListener('input', aplicar);
    });
  });

  grupo.querySelectorAll('[data-botones="radio"]').forEach((el) => {
    el.addEventListener('change', () => estado.setBotones({ radio: el.value }));
  });
  grupo.querySelectorAll('[data-tarjetas="radio"]').forEach((el) => {
    el.addEventListener('change', () => estado.setTarjetas({ radio: el.value }));
  });

  const orden = grupo.querySelector('[data-orden]');
  if (orden) {
    orden.querySelectorAll('[data-mover]').forEach((el) => {
      el.addEventListener('click', () => {
        const item = el.closest('.pf-ed-orden__item');
        const items = Array.from(orden.querySelectorAll('.pf-ed-orden__item'));
        const indice = items.indexOf(item);
        const delta = Number(el.dataset.mover);
        const nuevoIndice = indice + delta;
        if (nuevoIndice < 0 || nuevoIndice >= items.length) return;
        const codigos = items.map((it) => it.dataset.codigo);
        const [movido] = codigos.splice(indice, 1);
        codigos.splice(nuevoIndice, 0, movido);
        estado.setSeccionOrden(codigos);
      });
    });
  }
}
